/**
 * Swim-themed Empty State Component
 *
 * A reusable empty state component with swim school theming.
 * Provides encouraging messaging and clear next-step guidance.
 */

import { Box, Button, EmptyState, Icon, VStack } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import {
  FiCalendar,
  FiClipboard,
  FiCreditCard,
  FiDroplet,
  FiFileText,
  FiHome,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi"

// SVG wave decoration for empty states
const WaveDecoration = () => (
  <Box
    position="absolute"
    bottom={0}
    left={0}
    right={0}
    height="60px"
    overflow="hidden"
    opacity={0.15}
    pointerEvents="none"
  >
    <svg
      viewBox="0 0 1200 120"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      aria-hidden="true"
    >
      <path
        d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
        fill="#0891B2"
      />
      <path
        d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
        fill="#0EA5E9"
        opacity="0.5"
      />
    </svg>
  </Box>
)

// Swim-themed icons mapped to resource types
const resourceIcons: Record<string, ReactNode> = {
  swimmers: <FiUser />,
  students: <FiUser />,
  families: <FiHome />,
  households: <FiHome />,
  enrollments: <FiClipboard />,
  classes: <FiDroplet />,
  classTemplates: <FiDroplet />,
  sessions: <FiCalendar />,
  classSessions: <FiCalendar />,
  attendance: <FiClipboard />,
  attendances: <FiClipboard />,
  invoices: <FiCreditCard />,
  payments: <FiCreditCard />,
  files: <FiFileText />,
  reports: <FiFileText />,
  users: <FiUsers />,
  default: <FiSearch />,
}

// Swim-themed titles and descriptions
const resourceContent: Record<
  string,
  { title: string; description: string; actionLabel?: string }
> = {
  swimmers: {
    title: "Ready to make a splash?",
    description:
      "Add your swimmers to get started with swim lessons. We can't wait to see them in the pool!",
    actionLabel: "Add Swimmer",
  },
  students: {
    title: "Ready to make a splash?",
    description:
      "Add your students to get started with swim lessons. We can't wait to see them in the pool!",
    actionLabel: "Add Student",
  },
  families: {
    title: "No families yet",
    description:
      "Families will appear here once they register. Each family can have multiple swimmers.",
    actionLabel: "Add Family",
  },
  households: {
    title: "No families yet",
    description:
      "Families will appear here once they register. Each family can have multiple swimmers.",
    actionLabel: "Add Family",
  },
  enrollments: {
    title: "Dive into your first class!",
    description:
      "Browse our class offerings and enroll your swimmers. The water is warm and waiting!",
    actionLabel: "Enroll Now",
  },
  classes: {
    title: "No classes scheduled",
    description:
      "Class offerings will appear here. Check back soon for new swim sessions!",
    actionLabel: "Add Class",
  },
  classTemplates: {
    title: "No class offerings yet",
    description:
      "Create class templates to define your swim programs, levels, and schedules.",
    actionLabel: "Create Class",
  },
  sessions: {
    title: "No session terms yet",
    description:
      "Session terms organize your classes by semester or season. Create your first term to get started.",
    actionLabel: "Create Session",
  },
  classSessions: {
    title: "No classes on the schedule",
    description:
      "Individual class meetings will appear here once sessions are generated from your class templates.",
    actionLabel: "Generate Sessions",
  },
  attendance: {
    title: "No attendance records",
    description:
      "Attendance records will appear here as swimmers check in for their classes.",
  },
  attendances: {
    title: "No attendance records",
    description:
      "Attendance records will appear here as swimmers check in for their classes.",
  },
  invoices: {
    title: "All caught up!",
    description:
      "No invoices to display. Payment records will appear here when enrollments are created.",
  },
  payments: {
    title: "All caught up!",
    description: "No outstanding payments. You're all set!",
  },
  files: {
    title: "No files uploaded",
    description:
      "Upload documents, photos, or other files to keep everything organized in one place.",
    actionLabel: "Upload File",
  },
  reports: {
    title: "No reports available",
    description:
      "Reports will be generated based on your swim school data. Check back after you have some activity.",
  },
  users: {
    title: "No users found",
    description: "User accounts will appear here once they register.",
    actionLabel: "Add User",
  },
  search: {
    title: "No results found",
    description:
      "Try adjusting your search terms or filters to find what you're looking for.",
  },
  default: {
    title: "Nothing here yet",
    description: "Data will appear here once it's available.",
  },
}

export interface SwimEmptyStateProps {
  /** Resource type for contextual theming */
  resource?:
    | "swimmers"
    | "students"
    | "families"
    | "households"
    | "enrollments"
    | "classes"
    | "classTemplates"
    | "sessions"
    | "classSessions"
    | "attendance"
    | "attendances"
    | "invoices"
    | "payments"
    | "files"
    | "reports"
    | "users"
    | "search"
    | "default"
  /** Custom title (overrides resource default) */
  title?: string
  /** Custom description (overrides resource default) */
  description?: string
  /** Custom icon (overrides resource default) */
  icon?: ReactNode
  /** Action button label */
  actionLabel?: string
  /** Action button link */
  actionTo?: string
  /** Action button click handler (alternative to link) */
  onAction?: () => void
  /** Whether to show the wave decoration */
  showWaves?: boolean
  /** Size variant */
  size?: "sm" | "md" | "lg"
}

export function SwimEmptyState({
  resource = "default",
  title,
  description,
  icon,
  actionLabel,
  actionTo,
  onAction,
  showWaves = true,
  size = "md",
}: SwimEmptyStateProps) {
  const content = resourceContent[resource] || resourceContent.default
  const resourceIcon = resourceIcons[resource] || resourceIcons.default

  const displayTitle = title || content.title
  const displayDescription = description || content.description
  const displayIcon = icon || resourceIcon
  const displayActionLabel = actionLabel || content.actionLabel

  const sizeStyles = {
    sm: { py: 6, iconSize: "lg", titleSize: "sm", descSize: "xs" },
    md: { py: 10, iconSize: "xl", titleSize: "md", descSize: "sm" },
    lg: { py: 16, iconSize: "2xl", titleSize: "lg", descSize: "md" },
  }

  const styles = sizeStyles[size]

  return (
    <Box
      position="relative"
      bg="ui.backgroundAlt"
      borderRadius="xl"
      overflow="hidden"
    >
      <EmptyState.Root py={styles.py}>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <Box
              p={4}
              bg="white"
              borderRadius="full"
              boxShadow="sm"
              color="ui.main"
            >
              <Icon fontSize={styles.iconSize}>{displayIcon}</Icon>
            </Box>
          </EmptyState.Indicator>
          <VStack textAlign="center" gap={2} maxW="md" px={4}>
            <EmptyState.Title
              fontSize={styles.titleSize}
              fontWeight="semibold"
              color="gray.700"
            >
              {displayTitle}
            </EmptyState.Title>
            <EmptyState.Description fontSize={styles.descSize} color="gray.500">
              {displayDescription}
            </EmptyState.Description>

            {(displayActionLabel && actionTo) || onAction ? (
              <Box mt={4}>
                {actionTo ? (
                  <Link to={actionTo} {...({} as any)}>
                    <Button colorPalette="cyan" size={size}>
                      {displayActionLabel}
                    </Button>
                  </Link>
                ) : (
                  <Button colorPalette="cyan" size={size} onClick={onAction}>
                    {displayActionLabel}
                  </Button>
                )}
              </Box>
            ) : null}
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>

      {showWaves && <WaveDecoration />}
    </Box>
  )
}

export default SwimEmptyState
